import { useState, useEffect } from 'react';
import {
  Card,
  Col,
  Row,
  Form,
  FloatingLabel,
  Alert,
  Spinner,
  Modal
} from 'react-bootstrap';
import {
  faPhone,
  faMapPin,
  faBriefcase,
  faCertificate,
  faFileAlt,
  faEdit,
  faSave,
  faTimes,
  faDownload,
  faEye
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Button from 'components/base/Button';
import AvatarUpload from 'components/common/AvatarUpload';
import LocationAutocomplete from 'components/common/LocationAutocomplete';
import { Coordinates } from 'features/location/googleLocation';
import classNames from 'classnames';
import team15 from 'assets/img/team/avatar-rounded.webp';
import {
  getProfile,
  updateProfile,
  downloadDocument,
  viewDocument
} from 'features/profile/profileApi';

const LocumProfilePage = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [profileData, setProfileData] = useState<any>(null);
  const [formData, setFormData] = useState<any>({});
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [selectedDocument, setSelectedDocument] = useState<any>(null);
  const [showDocViewer, setShowDocViewer] = useState(false);
  const [selectedCoordinates, setSelectedCoordinates] =
    useState<Coordinates | null>(null);

  // Fetch profile data
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const data = await getProfile();
        setProfileData(data);
        setFormData(data.profile);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load profile');
      } finally {
        setIsLoading(false);
      }
    };

    fetchProfile();
  }, []);

  // Handle form input change
  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]:
        name.includes('years') || name.includes('distance')
          ? parseFloat(value)
          : value
    });
  };

  // Handle save
  const handleSave = async () => {
    try {
      setIsSaving(true);
      setError(null);

      // Include coordinates in form data if selected
      const dataToSave = { ...formData };
      if (selectedCoordinates) {
        dataToSave.latitude = selectedCoordinates.lat;
        dataToSave.longitude = selectedCoordinates.lng;
      }

      const result = await updateProfile(dataToSave);
      setSuccess('Profile updated successfully');
      setIsEditing(false);
      setProfileData(result);
      setFormData(result.profile);
      setSelectedCoordinates(null);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update profile');
    } finally {
      setIsSaving(false);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    setFormData(profileData?.profile || {});
    setIsEditing(false);
    setError(null);
    setSelectedCoordinates(null);
  };

  // Handle document download
  const handleDocumentDownload = async (document: any) => {
    try {
      console.log('Starting download for:', document.document_name);
      await downloadDocument(document.document_name);
      console.log('Download successful for:', document.document_name);
      setSuccess('Document downloaded successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      const errorMsg =
        err instanceof Error ? err.message : 'Failed to download document';
      console.error('Download error:', errorMsg);
      setError(errorMsg);
    }
  };

  // Handle document view
  const handleDocumentView = (document: any) => {
    viewDocument(document.document_name);
  };

  if (isLoading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: '400px' }}
      >
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  if (!profileData) {
    return <Alert variant="danger">Failed to load profile</Alert>;
  }

  return (
    <div>
      {error && (
        <Alert variant="danger" onClose={() => setError(null)} dismissible>
          {error}
        </Alert>
      )}
      {success && (
        <Alert variant="success" onClose={() => setSuccess(null)} dismissible>
          {success}
        </Alert>
      )}

      {/* Profile Header Card */}
      <Card className="mb-4">
        <Card.Body>
          <Row className="align-items-center g-4">
            <Col xs={12} sm="auto">
              <AvatarUpload size="5xl" src={team15} />
            </Col>
            <Col xs={12} sm>
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <h2 className="mb-1">{profileData.user.name}</h2>
                  <p className="text-body-secondary mb-2">
                    <FontAwesomeIcon icon={faPhone} className="me-2" />
                    {isEditing ? (
                      <Form.Control
                        type="tel"
                        name="phone"
                        value={formData.phone || ''}
                        onChange={handleInputChange}
                        className="d-inline-block"
                        style={{ width: '200px' }}
                      />
                    ) : (
                      profileData?.profile?.phone || 'N/A'
                    )}
                  </p>
                  <p className="text-body-secondary mb-0">
                    <FontAwesomeIcon icon={faMapPin} className="me-2" />
                    {profileData?.profile?.location || 'Not set'}
                  </p>
                </div>
                <div>
                  {!isEditing ? (
                    <Button
                      variant="phoenix-primary"
                      onClick={() => setIsEditing(true)}
                      startIcon={<FontAwesomeIcon icon={faEdit} />}
                      size="sm"
                    >
                      Edit Profile
                    </Button>
                  ) : (
                    <div className="d-flex gap-2">
                      <Button
                        variant="success"
                        onClick={handleSave}
                        loading={isSaving}
                        startIcon={<FontAwesomeIcon icon={faSave} />}
                        size="sm"
                      >
                        Save
                      </Button>
                      <Button
                        variant="secondary"
                        onClick={handleCancel}
                        startIcon={<FontAwesomeIcon icon={faTimes} />}
                        size="sm"
                      >
                        Cancel
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Professional Details Card */}
      <Card className="mb-4">
        <Card.Header className="bg-light">
          <Card.Title className="mb-0">
            <FontAwesomeIcon icon={faBriefcase} className="me-2 text-primary" />
            Professional Details
          </Card.Title>
        </Card.Header>
        <Card.Body>
          <Row className="g-3">
            <Col md={6}>
              <div className="form-icon-container">
                <FloatingLabel
                  controlId="qualifications"
                  label="Qualifications"
                >
                  <Form.Control
                    as="textarea"
                    name="qualifications"
                    value={formData.qualifications || ''}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    placeholder="Qualifications"
                    style={{ minHeight: '50px' }}
                  />
                </FloatingLabel>
              </div>
            </Col>
            <Col md={6}>
              <div className="form-icon-container">
                <FloatingLabel
                  controlId="experience_years"
                  label="Years of Experience"
                >
                  <Form.Control
                    type="number"
                    name="experience_years"
                    value={formData.experience_years || ''}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    placeholder="Years of Experience"
                    className="input-spin-none"
                  />
                </FloatingLabel>
              </div>
            </Col>
            <Col md={6}>
              <div className="form-icon-container">
                <FloatingLabel controlId="locum_role" label="Locum Role">
                  {isEditing ? (
                    <Form.Select
                      name="locum_role"
                      value={formData?.locum_role || 'pharmacist'}
                      onChange={handleInputChange}
                    >
                      <option value="pharmacist">Pharmacist</option>
                      <option value="technician">Technician</option>
                    </Form.Select>
                  ) : (
                    <Form.Control
                      type="text"
                      value={
                        formData?.locum_role === 'pharmacist'
                          ? 'Pharmacist'
                          : 'Technician'
                      }
                      disabled
                    />
                  )}
                </FloatingLabel>
              </div>
            </Col>
            {/* <Col md={6}>
  <div className="form-icon-container">
    <FloatingLabel controlId="locum_role" label="Locum Role">
      <Form.Control
        type="text"
        value={
          formData?.locum_role === 'pharmacist'
            ? 'Pharmacist'
            : 'Technician'
        }
        disabled // ✅ always disabled
       
      />
    </FloatingLabel>
  </div>
</Col> */}
          </Row>
        </Card.Body>
      </Card>

      {/* Location Card */}
      <Card className="mb-4">
        <Card.Header className="bg-light">
          <Card.Title className="mb-0">
            <FontAwesomeIcon icon={faMapPin} className="me-2 text-primary" />
            Service Location
          </Card.Title>
        </Card.Header>
        <Card.Body>
          <Row className="g-3">
            <Col md={6}>
              {isEditing ? (
                <LocationAutocomplete
                  value={formData.location || ''}
                  onChange={location => setFormData({ ...formData, location })}
                  onCoordinatesChange={setSelectedCoordinates}
                  label="Location"
                  placeholder="Search location..."
                  required={false}
                />
              ) : (
                <div className="form-icon-container">
                  <FloatingLabel controlId="location" label="Location">
                    <Form.Control
                      type="text"
                      name="location"
                      value={formData.location || ''}
                      disabled
                      placeholder="Location"
                    />
                  </FloatingLabel>
                </div>
              )}
            </Col>
            <Col md={6}>
              <div className="form-icon-container">
                <FloatingLabel
                  controlId="travel_distance"
                  label="Travel Distance (km)"
                >
                  <Form.Control
                    type="number"
                    name="travel_distance"
                    value={formData.travel_distance || ''}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    placeholder="Travel Distance"
                    className="input-spin-none"
                  />
                </FloatingLabel>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Documents Card */}
      <Card>
        <Card.Header className="bg-light">
          <Card.Title className="mb-0">
            <FontAwesomeIcon icon={faFileAlt} className="me-2 text-primary" />
            Documents ({profileData.documents.length})
          </Card.Title>
        </Card.Header>
        <Card.Body>
          {profileData.documents.length > 0 ? (
            <div className="list-group">
              {profileData.documents.map((doc: any) => (
                <div
                  key={doc.id}
                  className="list-group-item d-flex justify-content-between align-items-center"
                >
                  <div>
                    <h6 className="mb-1 d-flex align-items-center">
                      <FontAwesomeIcon
                        icon={faCertificate}
                        className="me-2 text-warning"
                      />
                      {doc.doc_type.replace(/_/g, ' ').toUpperCase()}
                    </h6>
                    <small className="text-body-secondary">
                      {doc.document_name}
                    </small>
                  </div>
                  <div className="d-flex gap-2">
                    <Button
                      variant="phoenix-secondary"
                      size="sm"
                      startIcon={<FontAwesomeIcon icon={faEye} />}
                      onClick={() => handleDocumentView(doc)}
                    >
                      View
                    </Button>
                    <Button
                      variant="phoenix-primary"
                      size="sm"
                      startIcon={<FontAwesomeIcon icon={faDownload} />}
                      onClick={() => handleDocumentDownload(doc)}
                    >
                      Download
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <Alert variant="info" className="mb-0">
              No documents uploaded yet
            </Alert>
          )}
        </Card.Body>
      </Card>

      {/* Document Viewer Modal */}
      <Modal
        show={showDocViewer}
        onHide={() => setShowDocViewer(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {selectedDocument?.doc_type.replace(/_/g, ' ').toUpperCase()}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedDocument && (
            <div className="text-center">
              <p className="mb-3 text-body-secondary">
                {selectedDocument.document_name}
              </p>
              <div
                style={{
                  border: '1px solid #dee2e6',
                  borderRadius: '8px',
                  padding: '20px',
                  backgroundColor: '#f8f9fa',
                  minHeight: '400px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexDirection: 'column'
                }}
              >
                <FontAwesomeIcon
                  icon={faFileAlt}
                  size="3x"
                  className="text-muted mb-3"
                />
                <p className="text-muted mb-2">
                  <strong>{selectedDocument.document_name}</strong>
                </p>
                <p className="text-muted small mb-4">
                  Click the download button to view or download this document.
                </p>
                <Button
                  variant="phoenix-primary"
                  startIcon={<FontAwesomeIcon icon={faDownload} />}
                  onClick={() => {
                    handleDocumentDownload(selectedDocument);
                  }}
                >
                  Open Document
                </Button>
              </div>
            </div>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default LocumProfilePage;
